// client/src/pages/SettingsPage.js
// Organisation settings page — accessible via /settings from the sidebar.
// Sections: org details (name, slug, plan), members list, invite user.
// All mutations are admin-only; viewers see a read-only view.
// UPDATED Jun 2026: Initial build. Clerk auth, custom org DB layer.

import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import SidebarLayout from '../components/SidebarLayout';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'https://geojetpack-production.up.railway.app'}/api`;

const colors = {
  primary:   '#39393a',
  teal:      '#048ba8',
  coral:     '#e76f51',
  green:     '#01796f',
  amber:     '#f4a261',
  textMuted: '#6b7280',
  border:    '#e5e7eb',
  bgSubtle:  '#f9fafb',
};

const PLAN_LABELS = {
  launchpad:   'Launchpad',
  elevator:    'Elevator',
  skyscraper:  'Skyscraper',
  stratosphere:'Stratosphere',
};

const ROLE_LABELS = {
  admin:  'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
};

const ROLE_COLORS = {
  admin:  { bg: '#EEEDFE', text: '#3C3489' },
  editor: { bg: '#E1F5EE', text: '#085041' },
  viewer: { bg: '#F1EFE8', text: '#444441' },
};

const STATUS_COLORS = {
  active:  { bg: '#EAF3DE', text: '#3B6D11' },
  invited: { bg: '#FAEEDA', text: '#854F0B' },
};

// ── Primitives ────────────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <div style={{
    fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em',
    textTransform: 'uppercase', color: colors.textMuted, marginBottom: '12px'
  }}>
    {children}
  </div>
);

const Section = ({ children, style = {} }) => (
  <div style={{
    backgroundColor: 'white', border: `1px solid ${colors.border}`,
    borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', ...style
  }}>
    {children}
  </div>
);

const SectionHeader = ({ children, action }) => (
  <div style={{
    padding: '14px 20px', borderBottom: `1px solid ${colors.border}`,
    fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em',
    textTransform: 'uppercase', color: colors.textMuted,
    backgroundColor: colors.bgSubtle, display: 'flex',
    alignItems: 'center', justifyContent: 'space-between'
  }}>
    <span>{children}</span>
    {action}
  </div>
);

const Row = ({ label, value, valueColor, last = false }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 20px', borderBottom: last ? 'none' : `1px solid ${colors.border}`,
  }}>
    <span style={{ fontSize: '13px', color: colors.textMuted }}>{label}</span>
    <span style={{ fontSize: '13px', fontWeight: '500', color: valueColor || colors.primary }}>
      {value || '—'}
    </span>
  </div>
);

const Badge = ({ label, colors: c }) => (
  <span style={{
    fontSize: '11px', fontWeight: '600', padding: '2px 8px',
    borderRadius: '4px', backgroundColor: c.bg, color: c.text
  }}>
    {label}
  </span>
);

const Pill = ({ plan }) => {
  const planColors = {
    launchpad:    { bg: '#E6F1FB', text: '#0C447C' },
    elevator:     { bg: '#E1F5EE', text: '#085041' },
    skyscraper:   { bg: '#EEEDFE', text: '#3C3489' },
    stratosphere: { bg: '#FAEEDA', text: '#854F0B' },
  };
  const c = planColors[plan] || planColors.launchpad;
  return (
    <span style={{
      fontSize: '11px', fontWeight: '600', padding: '3px 10px',
      borderRadius: '4px', backgroundColor: c.bg, color: c.text
    }}>
      {PLAN_LABELS[plan] || plan}
    </span>
  );
};

const TealButton = ({ children, onClick, disabled, style = {} }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      padding: '8px 16px', fontSize: '13px', fontWeight: '500',
      color: disabled ? colors.textMuted : 'white',
      backgroundColor: disabled ? colors.border : colors.teal,
      border: 'none', borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s', fontFamily: "'Work Sans', sans-serif", ...style
    }}
  >
    {children}
  </button>
);

const GhostButton = ({ children, onClick, danger = false, style = {} }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '5px 10px', fontSize: '12px', fontWeight: '500',
        color: danger
          ? (hovered ? '#991b1b' : colors.coral)
          : (hovered ? colors.primary : colors.textMuted),
        backgroundColor: hovered ? (danger ? '#fef2f2' : colors.bgSubtle) : 'transparent',
        border: `1px solid ${danger ? (hovered ? '#fca5a5' : '#fcd4c8') : colors.border}`,
        borderRadius: '5px', cursor: 'pointer',
        transition: 'all 0.15s', fontFamily: "'Work Sans', sans-serif", ...style
      }}
    >
      {children}
    </button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

function SettingsPage({ campaigns, apiBaseUrl: propApiBaseUrl }) {
  const { user } = useUser();
  const apiBase  = propApiBaseUrl || API_BASE_URL;

  const [org,          setOrg]          = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [saveMsg,      setSaveMsg]      = useState(null);

  // Org name edit
  const [editingName,  setEditingName]  = useState(false);
  const [nameInput,    setNameInput]    = useState('');

  // Invite form
  const [inviteEmail,  setInviteEmail]  = useState('');
  const [inviteRole,   setInviteRole]   = useState('viewer');
  const [inviting,     setInviting]     = useState(false);
  const [inviteMsg,    setInviteMsg]    = useState(null);

  const clerkUserId = user?.id;

  const loadOrg = useCallback(async () => {
    if (!clerkUserId) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${apiBase}/org/mine`, {
        headers: { 'x-user-id': clerkUserId }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load organisation');
      setOrg(data.org);
      setNameInput(data.org.name);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clerkUserId, apiBase]);

  useEffect(() => { loadOrg(); }, [loadOrg]);

  const isOwner = org?.owner_user_id === clerkUserId;
  const callerMember = org?.members?.find(m => m.clerk_user_id === clerkUserId);
  const isAdmin = isOwner || callerMember?.role === 'admin';

  // ── Save org name ───────────────────────────────────────────────────────────
  const saveName = async () => {
    if (!nameInput.trim() || nameInput === org.name) { setEditingName(false); return; }
    setSaving(true);
    try {
      const res  = await fetch(`${apiBase}/org/${org.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-user-id': clerkUserId },
        body: JSON.stringify({ name: nameInput.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setOrg(prev => ({ ...prev, name: data.org.name, slug: data.org.slug }));
      setEditingName(false);
      flash(setSaveMsg, 'Saved');
    } catch (err) {
      flash(setSaveMsg, `Error: ${err.message}`, true);
    } finally {
      setSaving(false);
    }
  };

  // ── Invite user ─────────────────────────────────────────────────────────────
  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteMsg(null);
    try {
      const res  = await fetch(`${apiBase}/org/${org.id}/members/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': clerkUserId },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.detail);
      setInviteEmail('');
      setInviteRole('viewer');
      flash(setInviteMsg, `Invite sent to ${inviteEmail.trim()}`);
      await loadOrg();
    } catch (err) {
      flash(setInviteMsg, `Error: ${err.message}`, true);
    } finally {
      setInviting(false);
    }
  };

  // ── Change member role ──────────────────────────────────────────────────────
  const changeRole = async (memberId, newRole) => {
    try {
      const res = await fetch(`${apiBase}/org/${org.id}/members/${memberId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-user-id': clerkUserId },
        body: JSON.stringify({ role: newRole })
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setOrg(prev => ({
        ...prev,
        members: prev.members.map(m => m.id === memberId ? { ...m, role: newRole } : m)
      }));
    } catch (err) {
      alert(`Could not change role: ${err.message}`);
    }
  };

  // ── Remove member ───────────────────────────────────────────────────────────
  const removeMember = async (memberId, email) => {
    if (!window.confirm(`Remove ${email} from this organisation?`)) return;
    try {
      const res = await fetch(`${apiBase}/org/${org.id}/members/${memberId}`, {
        method: 'DELETE',
        headers: { 'x-user-id': clerkUserId }
      });
      if (!res.ok) throw new Error((await res.json()).error);
      setOrg(prev => ({
        ...prev,
        members: prev.members.filter(m => m.id !== memberId)
      }));
    } catch (err) {
      alert(`Could not remove member: ${err.message}`);
    }
  };

  // ── Flash helper ────────────────────────────────────────────────────────────
  const flash = (setter, msg, isError = false) => {
    setter({ text: msg, error: isError });
    setTimeout(() => setter(null), 3500);
  };

  // ── Loading / error states ──────────────────────────────────────────────────
  if (loading) {
    return (
      <SidebarLayout campaigns={campaigns || []} apiBaseUrl={apiBase}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              border: `3px solid ${colors.border}`, borderTopColor: colors.teal,
              animation: 'spin 0.8s linear infinite', margin: '0 auto 12px'
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ fontSize: '13px', color: colors.textMuted }}>Loading settings…</p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (error) {
    return (
      <SidebarLayout campaigns={campaigns || []} apiBaseUrl={apiBase}>
        <div style={{ padding: '32px' }}>
          <div style={{ border: `1px solid #fca5a5`, borderRadius: '8px', padding: '20px', backgroundColor: '#fef2f2' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>Could not load settings</div>
            <div style={{ fontSize: '13px', color: '#b91c1c' }}>{error}</div>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  const activeMembers  = (org?.members || []).filter(m => m.status === 'active');
  const invitedMembers = (org?.members || []).filter(m => m.status === 'invited');

  return (
    <SidebarLayout campaigns={campaigns || []} apiBaseUrl={apiBase}>
      <div style={{
        padding: '28px 32px', maxWidth: '860px',
        fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, sans-serif"
      }}>

        {/* ── Organisation details ──────────────────────────────────────────── */}
        <SectionLabel>Organisation</SectionLabel>

        <Section>
          <SectionHeader
            action={
              isAdmin && !editingName
                ? <GhostButton onClick={() => setEditingName(true)}>Edit name</GhostButton>
                : null
            }
          >
            Details
          </SectionHeader>

          {/* Name row — inline edit */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 20px', borderBottom: `1px solid ${colors.border}`
          }}>
            <span style={{ fontSize: '13px', color: colors.textMuted }}>Organisation name</span>
            {editingName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  autoFocus
                  style={{
                    fontSize: '13px', padding: '5px 10px', borderRadius: '6px',
                    border: `1px solid ${colors.teal}`, outline: 'none',
                    fontFamily: "'Work Sans', sans-serif", width: '220px'
                  }}
                />
                <TealButton onClick={saveName} disabled={saving}>{saving ? 'Saving…' : 'Save'}</TealButton>
                <GhostButton onClick={() => { setEditingName(false); setNameInput(org.name); }}>Cancel</GhostButton>
              </div>
            ) : (
              <span style={{ fontSize: '13px', fontWeight: '500', color: colors.primary }}>{org?.name}</span>
            )}
          </div>
          {saveMsg && (
            <div style={{
              padding: '8px 20px', fontSize: '12px',
              color: saveMsg.error ? '#991b1b' : colors.green,
              backgroundColor: saveMsg.error ? '#fef2f2' : '#EAF3DE',
              borderBottom: `1px solid ${colors.border}`
            }}>
              {saveMsg.text}
            </div>
          )}

          <Row label="Slug (URL identifier)" value={org?.slug} />
          <Row
            label="Plan"
            value={<Pill plan={org?.plan} />}
          />
          <Row
            label="Owner"
            value={
              isOwner
                ? `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.primaryEmailAddress?.emailAddress || clerkUserId
                : org?.owner_user_id
            }
          />
          <Row
            label="Your role"
            value={
              isOwner
                ? <Badge label="Admin" colors={ROLE_COLORS.admin} />
                : <Badge label={ROLE_LABELS[callerMember?.role] || '—'} colors={ROLE_COLORS[callerMember?.role] || ROLE_COLORS.viewer} />
            }
            last
          />
        </Section>

        {/* ── Team members ─────────────────────────────────────────────────── */}
        <SectionLabel>Team</SectionLabel>

        <Section>
          <SectionHeader>
            Active members · {activeMembers.length}
          </SectionHeader>

          {activeMembers.length === 0 ? (
            <div style={{ padding: '20px', fontSize: '13px', color: colors.textMuted, fontStyle: 'italic' }}>
              No active members yet.
            </div>
          ) : (
            activeMembers.map((m, i) => {
              const isMe      = m.clerk_user_id === clerkUserId;
              const isOrgOwner = org.owner_user_id === m.clerk_user_id;
              const last      = i === activeMembers.length - 1;
              return (
                <div
                  key={m.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 20px', borderBottom: last ? 'none' : `1px solid ${colors.border}`
                  }}
                >
                  {/* Avatar */}
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    backgroundColor: '#E6F1FB', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '12px', fontWeight: '600',
                    color: '#0C447C', flexShrink: 0
                  }}>
                    {(m.email?.[0] || '?').toUpperCase()}
                  </div>

                  {/* Email */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: colors.primary, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {m.email || clerkUserId}
                      {isMe && <span style={{ fontSize: '11px', color: colors.textMuted, fontWeight: '400' }}>(you)</span>}
                      {isOrgOwner && <span style={{ fontSize: '11px', color: colors.textMuted, fontWeight: '400' }}>· owner</span>}
                    </div>
                    {m.joined_at && (
                      <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '1px' }}>
                        Joined {new Date(m.joined_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    )}
                  </div>

                  {/* Role selector (admin only, can't change yourself) */}
                  {isAdmin && !isMe && !isOrgOwner ? (
                    <select
                      value={m.role}
                      onChange={e => changeRole(m.id, e.target.value)}
                      style={{
                        fontSize: '12px', padding: '4px 8px', borderRadius: '5px',
                        border: `1px solid ${colors.border}`, backgroundColor: 'white',
                        color: colors.primary, cursor: 'pointer',
                        fontFamily: "'Work Sans', sans-serif"
                      }}
                    >
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  ) : (
                    <Badge label={ROLE_LABELS[m.role] || m.role} colors={ROLE_COLORS[m.role] || ROLE_COLORS.viewer} />
                  )}

                  {/* Remove button */}
                  {isAdmin && !isMe && !isOrgOwner && (
                    <GhostButton danger onClick={() => removeMember(m.id, m.email)}>
                      Remove
                    </GhostButton>
                  )}
                </div>
              );
            })
          )}
        </Section>

        {/* ── Pending invites ───────────────────────────────────────────────── */}
        {invitedMembers.length > 0 && (
          <Section>
            <SectionHeader>Pending invites · {invitedMembers.length}</SectionHeader>
            {invitedMembers.map((m, i) => (
              <div
                key={m.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '12px 20px',
                  borderBottom: i === invitedMembers.length - 1 ? 'none' : `1px solid ${colors.border}`
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  backgroundColor: '#FAEEDA', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '12px', fontWeight: '600',
                  color: '#854F0B', flexShrink: 0
                }}>
                  {(m.email?.[0] || '?').toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: colors.primary }}>{m.email}</div>
                  {m.invited_at && (
                    <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '1px' }}>
                      Invited {new Date(m.invited_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  )}
                </div>
                <Badge label={ROLE_LABELS[m.role] || m.role} colors={ROLE_COLORS[m.role] || ROLE_COLORS.viewer} />
                <Badge label="Pending" colors={STATUS_COLORS.invited} />
                {isAdmin && (
                  <GhostButton danger onClick={() => removeMember(m.id, m.email)}>
                    Cancel
                  </GhostButton>
                )}
              </div>
            ))}
          </Section>
        )}

        {/* ── Invite form ───────────────────────────────────────────────────── */}
        {isAdmin && (
          <>
            <SectionLabel>Invite someone</SectionLabel>
            <Section>
              <SectionHeader>Send an invitation</SectionHeader>
              <div style={{ padding: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: colors.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Email address
                  </label>
                  <input
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') sendInvite(); }}
                    style={{
                      width: '100%', fontSize: '13px', padding: '8px 12px',
                      borderRadius: '6px', border: `1px solid ${colors.border}`,
                      outline: 'none', fontFamily: "'Work Sans', sans-serif",
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ minWidth: '130px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: colors.textMuted, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Role
                  </label>
                  <select
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value)}
                    style={{
                      width: '100%', fontSize: '13px', padding: '8px 10px',
                      borderRadius: '6px', border: `1px solid ${colors.border}`,
                      backgroundColor: 'white', fontFamily: "'Work Sans', sans-serif",
                      cursor: 'pointer'
                    }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <TealButton onClick={sendInvite} disabled={inviting || !inviteEmail.trim()} style={{ alignSelf: 'flex-end', padding: '8px 20px' }}>
                  {inviting ? 'Sending…' : 'Send invite'}
                </TealButton>
              </div>

              {inviteMsg && (
                <div style={{
                  margin: '0 20px 16px', padding: '10px 14px', borderRadius: '6px',
                  fontSize: '13px',
                  color: inviteMsg.error ? '#991b1b' : colors.green,
                  backgroundColor: inviteMsg.error ? '#fef2f2' : '#EAF3DE',
                  border: `1px solid ${inviteMsg.error ? '#fca5a5' : '#C0DD97'}`
                }}>
                  {inviteMsg.text}
                </div>
              )}

              {/* Role legend */}
              <div style={{
                margin: '0 20px 16px', padding: '12px 16px', borderRadius: '6px',
                backgroundColor: colors.bgSubtle, border: `1px solid ${colors.border}`
              }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: colors.textMuted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Role permissions</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  {[
                    { role: 'admin',  desc: 'Full access — manages billing, users, projects, and campaigns' },
                    { role: 'editor', desc: 'Can create and run campaigns, edit project settings' },
                    { role: 'viewer', desc: 'Read-only access to dashboards and reports' },
                  ].map(({ role, desc }) => (
                    <div key={role} style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <Badge label={ROLE_LABELS[role]} colors={ROLE_COLORS[role]} />
                      <span style={{ fontSize: '12px', color: colors.textMuted }}>{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Section>
          </>
        )}

      </div>
    </SidebarLayout>
  );
}

export default SettingsPage;
